/**
 * Per-item Data-Encryption Key (DEK) lifecycle.
 *
 * A DEK is a 256-bit AES-GCM key. The usable handle is a **non-extractable**
 * WebCrypto CryptoKey: it can encrypt/decrypt item payloads but its bytes can
 * never be exported, serialised, or read by JS. To still allow wrapping the DEK
 * under the UMK at rest, the raw key material is held alongside the CryptoKey in
 * a module-private `WeakMap` (not reachable via the public type, GC'd with the
 * key, never serialised). `wrapDek` reads from that map; `unwrapDek` re-imports
 * a fresh non-extractable handle and re-registers its material.
 *
 * DEK/UMK serialisation tripwire (R3): the branded `DEK` carries a `toJSON` that
 * throws — any accidental `JSON.stringify` of a wrapper object fails loud.
 */
import { aesGcmSeal, aesGcmOpen, dekWrapAad } from './aead.js';
import { KindByte } from './version.js';
import { toArrayBuffer } from './bytes.js';
import { DekUnwrapError, DekSerializationError } from './errors.js';
/** CryptoKey → its raw 32-byte material. WeakMap so it can never be enumerated. */
const dekMaterial = new WeakMap();
function trap() {
    throw new DekSerializationError();
}
async function importDek(raw) {
    const key = (await crypto.subtle.importKey('raw', toArrayBuffer(raw), { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']));
    dekMaterial.set(key, raw);
    // Defensive serialisation tripwire (R3). Non-enumerable so it never widens the type.
    Object.defineProperty(key, 'toJSON', { value: trap, enumerable: false });
    return key;
}
async function importUmkAsAesKey(umk) {
    return crypto.subtle.importKey('raw', toArrayBuffer(umk), { name: 'AES-GCM' }, false, [
        'encrypt',
        'decrypt',
    ]);
}
/** Generate a fresh non-extractable per-item DEK. */
export async function generateItemDek() {
    const raw = crypto.getRandomValues(new Uint8Array(32));
    return importDek(raw);
}
/** Wrap a DEK under the UMK (AES-GCM). AAD = version ‖ userId ‖ itemId ‖ 'dek-wrap'. */
export async function wrapDek(args) {
    const raw = dekMaterial.get(args.dek);
    if (raw === undefined) {
        // Only DEKs minted by this module carry material; foreign keys cannot be wrapped.
        throw new DekUnwrapError();
    }
    const umkKey = await importUmkAsAesKey(args.umk);
    const aad = dekWrapAad({ userId: args.userId, itemId: args.itemId });
    return aesGcmSeal(umkKey, KindByte.DekWrap, raw, aad);
}
/** Unwrap a DEK under the UMK into a fresh non-extractable handle. */
export async function unwrapDek(args) {
    const umkKey = await importUmkAsAesKey(args.umk);
    const aad = dekWrapAad({ userId: args.userId, itemId: args.itemId });
    const raw = await aesGcmOpen(umkKey, KindByte.DekWrap, args.wrapped, aad, () => new DekUnwrapError());
    return importDek(raw);
}
//# sourceMappingURL=dek.js.map